import {
  AfterContentInit,
  Component, ContentChildren,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output, QueryList, TemplateRef,
  ViewChild
} from '@angular/core';
import {ActionButtonComponent} from '../action-buttons/action-button.component';
import {debounceTime, Subject, Subscription} from 'rxjs';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {PrimeTemplate} from 'primeng/api';
import {NgIf, NgTemplateOutlet} from "@angular/common";
import {InputTextModule} from "primeng/inputtext";
import {FormsModule} from "@angular/forms";
import {ButtonDirective} from "primeng/button";
import {TooltipModule} from "primeng/tooltip";
import {DialogModule} from "primeng/dialog";
import {EditorModule} from "primeng/editor";
import {generateRandomString} from "../../../utils/string-utils";
import {RestApiService} from '../../../services/api/rest-api.service';
import type {ActionButtonType} from '../action-buttons/action-buttons.types';
import {LabelWidth} from '../../../../defaults';
import {Textarea} from 'primeng/textarea';
import {Ripple} from 'primeng/ripple';
import {FloatLabel} from 'primeng/floatlabel';

@UntilDestroy()
@Component({
  standalone: true,
  selector: 'app-input',
  templateUrl: './input.component.html',
  imports: [
    NgIf,
    InputTextModule,
    FormsModule,
    ButtonDirective,
    TooltipModule,
    ActionButtonComponent,
    NgTemplateOutlet,
    DialogModule,
    EditorModule,
    Textarea,
    FloatLabel
  ],
  styleUrls: ['./input.component.scss']
})
export class InputComponent implements OnInit, OnDestroy, AfterContentInit {

  private static inputElementCounter = 1;

  private readonly _inputElementId: number;

  get inputElementId(): string {
    return 'inputElement' + this._inputElementId;
  }

  @Input()
  type: InputType = 'text';

  @Input()
  minValue = 0;

  @Input()
  maxValue?: number = undefined;

  @Input()
  autocomplete: 'on' | 'off' = 'on';

  @Input()
  placeholder = '';

  @Input()
  valueClass = '';

  @Input()
  icon = '';

  @Input()
  label = '';

  @Input()
  useFloatLabel : false | 'in' | 'on' | 'over' = false;

  get hasLabel(): boolean {
    return this.label !== '';
  }

  @Input()
  maxLength = 256;

  @Input()
  ignoreMaxLength = false;

  @Input()
  showLength = true;

  @Input()
  minLength = 0;

  @Input()
  showMaximizeTextareaButton = false;

  maximizedTextArea = false;

  get showAddons(): boolean {
    return this.showLength && this.model || this.showMaximizeTextareaButton || this.customButtons != null;
  }

  customButtons!: TemplateRef<any>;

  @ContentChildren(PrimeTemplate) templates!: QueryList<any>;


  internalModelLength = 0;

  private _internalModel: any;

  set internalModel(value: any) {
    if (this.automaticCaps) {
      if (typeof value === 'string') {
        value = (value as string).toUpperCase();
      }
    }
    this._internalModel = value;
    this.debouncerModelChange.next(value);
  }

  get internalModel(): any {
    return this._internalModel;
  }

  private _originalModel: any;

  @Input()
  set model(value: any) {
    this._originalModel = value;
    this._internalModel = value;
    this.internalModelLength = value?.length;
  }

  get model(): any {
    return this._originalModel;
  }

  get isChanged(): boolean {
    return this.showChangedAlert && this._internalModel !== this._originalModel;
  }

  private debouncerModelChange: Subject<any> = new Subject<any>();

  private debouncerSubscription: Subscription | undefined = undefined;

  private _debounceDelay = 0;

  get debounceDelay(): number {
    return this._debounceDelay;
  }

  /**
   * Tempistica di debouncing
   * @param time se valorizzato a 0, il debouncing viene disattivato
   */
  @Input()
  set debounceDelay(time: number) {
    this._debounceDelay = time;
    if (this.debouncerSubscription) {
      this.debouncerSubscription.unsubscribe();
      this.debouncerSubscription = undefined;
    }
    if (time > 0) {
      this.debouncerSubscription = this.debouncerModelChange
        .pipe(untilDestroyed(this), debounceTime(time))
        .subscribe(value => {
          this.internalModelLength = value?.length ?? 0;
          this.modelChange.emit(value);
        });
    }
  }

  @Output()
  modelChange: EventEmitter<any> = new EventEmitter<any>();


  @Input()
  canBeRandomized = false;

  @Input()
  labelWidth = LabelWidth;

  @Input()
  inputWidth = '200px';

  @Input()
  fullWidth = false;

  @Output() action: EventEmitter<string> = new EventEmitter();

  @Input()
  actionButtonLabel: string = '';

  @Input()
  actionButtonTooltip?: string = undefined;

  @Input()
  actionButtonType?: ActionButtonType = undefined;

  @Input()
  isActionTextButton: boolean = false;

  @Input()
  disabled = false;

  @Input()
  disabledActionButton = false;

  @Input()
  readOnly = false;

  @Input()
  required = false;

  @Input()
  showChangedAlert = false;

  @Input()
  autoresize = true;

  @ViewChild('textarea')
  textarea!: ElementRef<HTMLTextAreaElement>;

  @Input()
  automaticCaps = false;

  /**
   * Imposta automaticamente il focus sull'input quando la condizione e` vera dopo "autoFocusDelay" ms
   */
  @Input()
  set autoFocusOn(value: boolean) {
    if (value && this.inputElementId) {
      // TODO: utilizzare ViewChild di Angular...
      setTimeout(() => {
        document.getElementById(this.inputElementId)?.focus();
      }, this.autoFocusDelay);
    }
  }

  @Input()
  autoFocusDelay = 50;

  get hasActionButton() {
    return this.action.observers.length > 0;
  }

  constructor(private api: RestApiService) {
    this._inputElementId = ++InputComponent.inputElementCounter;
  }

  ngOnDestroy(): void {
  }

  ngOnInit(): void {
    this.debounceDelay = 200; // valore di default del debouncing time
  }

  ngAfterContentInit() {
    this.templates.forEach((item) => {
      switch (item.getType()) {
        case 'buttons':
          this.customButtons = item.template;
          break;
      }
    });
  }

  async randomizeContent() {
    // TODO: [SRK] implementare la chiamata al servizio per ottenere un testo random
    // const response = await this.api.tools.getRandomText();
    // this.model = response.data;

    // generate random string from lower and upper case letters and numbers
    this.model = generateRandomString(16);
  }

  scrollToBottom() {
    this.textarea.nativeElement.scrollTop = this.textarea.nativeElement.scrollHeight;
  }

  maximize() {
    this.maximizedTextArea = true;
  }
}

export type InputType = 'text' | 'textarea' | 'label' | 'number' | 'wysiwyg' | 'time' | 'password';
